import { useEffect, type JSX, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { Icon } from './Icon';

/**
 * Шторка снизу — то, чем на телефоне становится боковая панель.
 *
 * Панель свойств узла в 420 px и список камер в 200 px на экране шириной
 * 360 px не панели, а половина экрана: смотреть остаётся не на что.
 * Свернуть их в колонку под содержимым тоже не выход — выбрал узел на карте,
 * а его свойства оказались ниже карты, за экран прокрутки.
 *
 * Шторка решает ровно это: она приходит поверх, занимает столько, сколько
 * нужно её содержимому, и уходит, когда с ней закончили. Ниже планшета она
 * не используется вовсе — там панель стоит сбоку и никуда не девается.
 *
 * Высота ограничена долей экрана, а не содержимым: полностью закрытый
 * шторкой экран перестаёт отвечать на вопрос «а что там было», ради
 * которого её и открыли.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end">
      {/* Подложка закрывает шторку и не даёт нажать то, что под ней. */}
      <button
        type="button"
        aria-label={t('common.action.close')}
        onClick={onClose}
        className="flex-1 bg-black/60"
      />

      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        /*
          Поля безопасной зоны считаются здесь заново: шторка рисуется
          `fixed`, а поля заданы на `body` и на неё не распространяются —
          нижний край ушёл бы под полосу жеста.
        */
        className="flex max-h-[85dvh] min-h-0 flex-col rounded-t-lg border-t border-line bg-surface pb-[env(safe-area-inset-bottom)]"
      >
        <header className="flex h-11 flex-none items-center gap-2 border-b border-line px-3">
          {/*
            Полоска сверху — не украшение: по ней шторку узнают как шторку
            и понимают, что её можно закрыть, ещё до того, как найдут крестик.
          */}
          <div className="absolute inset-x-0 -top-2.5 flex justify-center">
            <div className="h-1 w-10 rounded-full bg-line-strong" />
          </div>

          <h3 className="min-w-0 flex-1 truncate text-lg font-semibold">{title}</h3>

          <button
            type="button"
            aria-label={t('common.action.close')}
            onClick={onClose}
            className="flex size-9 flex-none items-center justify-center rounded-sm text-dim hover:text-fg"
          >
            <Icon name="close" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </section>
    </div>
  );
}
